/**
 * API для работы с GIF через Giphy.
 *
 * 🔑 Получение API-ключа:
 * 1. Зарегистрируйтесь на https://developers.giphy.com/
 * 2. Создайте приложение → получите API Key
 * 3. Добавьте в файл .env в корне проекта:
 *    REACT_APP_GIPHY_API_KEY=ваш_ключ
 *
 * Если ключ не указан, API будет возвращать ошибку с инструкцией.
 */

const GIPHY_API_KEY = process.env.REACT_APP_GIPHY_API_KEY || '';
const GIPHY_BASE_URL = 'https://api.giphy.com/v1/gifs';

/**
 * Проверяет, настроен ли Giphy API.
 * @returns {boolean}
 */
export const isGiphyConfigured = () => {
    return !!GIPHY_API_KEY;
};

/**
 * Поиск GIF по запросу.
 * GET /v1/gifs/search
 * @param {string} query - Поисковый запрос
 * @param {Object} [options] - Дополнительные параметры
 * @param {number} [options.limit=20] - Количество результатов
 * @param {number} [options.offset=0] - Смещение
 * @returns {Promise<Array<{id, title, url, previewUrl, width, height}>>}
 */
export const searchGifs = async (query, { limit = 20, offset = 0 } = {}) => {
    if (!GIPHY_API_KEY) throw new Error('GIPHY_API_KEY не настроен');
    if (!query || !query.trim()) return [];
    try {
        const url = `${GIPHY_BASE_URL}/search?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(query.trim())}&limit=${limit}&offset=${offset}&rating=g&lang=ru`;
        const response = await fetch(url);
        if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
                throw new Error('Неверный или заблокированный GIPHY API ключ. Получите новый на https://developers.giphy.com/');
            }
            throw new Error(`Giphy API error: ${response.status}`);
        }
        const data = await response.json();
        return (data.data || []).map(mapGifResult);
    } catch (err) {
        console.error('[GiphyApi] Search failed:', err.message);
        throw err;
    }
};

/**
 * Получить популярные GIF (тренды).
 * GET /v1/gifs/trending
 * @param {Object} [options] - Дополнительные параметры
 * @param {number} [options.limit=20] - Количество результатов
 * @param {number} [options.offset=0] - Смещение
 * @returns {Promise<Array<{id, title, url, previewUrl, width, height}>>}
 */
export const getTrendingGifs = async ({ limit = 20, offset = 0 } = {}) => {
    if (!GIPHY_API_KEY) throw new Error('GIPHY_API_KEY не настроен');
    try {
        const url = `${GIPHY_BASE_URL}/trending?api_key=${GIPHY_API_KEY}&limit=${limit}&offset=${offset}&rating=g`;
        const response = await fetch(url);
        if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
                throw new Error('Неверный или заблокированный GIPHY API ключ. Получите новый на https://developers.giphy.com/');
            }
            throw new Error(`Giphy API error: ${response.status}`);
        }
        const data = await response.json();
        return (data.data || []).map(mapGifResult);
    } catch (err) {
        console.error('[GiphyApi] Trending failed:', err.message);
        throw err;
    }
};

/**
 * Маппинг результата Giphy в упрощённый формат.
 * @param {Object} gif - Объект GIF от Giphy
 * @returns {{id, title, url, previewUrl, width, height}}
 */
function mapGifResult(gif) {
    const images = gif.images || {};
    // Используем fixed_width для превью (удобный размер для сетки)
    const preview = images.fixed_width || images.fixed_width_small || images.downsized_medium || {};
    const original = images.original || {};
    return {
        id: gif.id,
        title: gif.title || '',
        url: original.url || '',
        previewUrl: preview.url || '',
        width: parseInt(preview.width, 10) || 200,
        height: parseInt(preview.height, 10) || 200,
    };
}